import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import WifiOffOutlinedIcon from '@mui/icons-material/WifiOffOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Link } from 'react-router-dom';
import type { SvgIconComponent } from '@mui/icons-material';
import { Reveal } from '@/components/Reveal';

type Pillar = {
  icon: SvgIconComponent;
  title: string;
  body: string;
  linkTo?: string;
  linkLabel?: string;
};

const pillars: Pillar[] = [
  {
    icon: HubOutlinedIcon,
    title: 'One platform, not five stitched tools',
    body: 'Ticketing, monitoring, security, and compliance share one tenant-isolated record — no Zapier glue, no reconciled timestamps, no tab-switching to find a device’s history.',
  },
  {
    icon: WifiOffOutlinedIcon,
    title: 'Outbound-only. Zero inbound site-firewall exposure.',
    body: 'The on-prem monitoring agent never listens for anything. No port to open, no firewall rule to justify at your next security review — at any client site, ever.',
  },
  {
    icon: SmartToyOutlinedIcon,
    title: 'Policy-governed autonomy',
    body: 'Low-risk, non-destructive work can be automated within tenant policy. Anything consequential — destructive, irreversible, or outside that policy — stays approval-gated, RBAC-enforced, and audited, full stop.',
  },
  {
    icon: VisibilityOutlinedIcon,
    title: 'We show you the real build status',
    body: 'No vaporware, no demo data dressed up as shipped. Every module is labeled by what’s actually true today, not what’s aspirational.',
    linkTo: '/roadmap',
    linkLabel: 'See the roadmap',
  },
];

export function Differentiators(): JSX.Element {
  return (
    <section className="relative overflow-hidden border-y border-white/10 py-16 md:py-24">
      <div
        aria-hidden
        className="animate-drift-slow pointer-events-none absolute -left-40 top-0 h-[420px] w-[420px] rounded-full bg-nexus-purple/[0.10] blur-3xl"
      />
      <div className="container-content relative">
        <Reveal>
          <div className="mx-auto mb-14 max-w-2xl text-center">
            <span className="text-xs font-bold uppercase tracking-[0.14em] text-nexus-purple-light">
              Built different, on purpose
            </span>
            <h2 className="mt-3 font-display text-3xl font-bold text-nexus-ink md:text-[36px]">
              Stop stitching your stack together.
            </h2>
            <p className="mt-4 text-white/70">
              Nexus isn’t an incremental improvement on the tools you already run — it’s a
              different set of bets on architecture, security, and how honest a vendor is
              willing to be.
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          {pillars.map((p, i) => {
            const Icon = p.icon;
            return (
              <Reveal key={p.title} delay={i * 90}>
                <div className="group h-full rounded-2xl border border-white/10 bg-white/[0.02] p-6 transition-all duration-200 hover:-translate-y-1 hover:border-nexus-purple/50 hover:shadow-[0_30px_60px_-30px_rgba(139,61,240,0.45)]">
                  <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-nexus-purple/[0.14] text-nexus-purple-light transition-transform duration-200 group-hover:scale-110">
                    <Icon fontSize="small" />
                  </div>
                  <h3 className="font-display text-[17px] font-semibold text-nexus-ink">{p.title}</h3>
                  <p className="mt-1.5 text-sm text-white/65">{p.body}</p>
                  {p.linkTo && (
                    <Button
                      component={Link}
                      to={p.linkTo}
                      size="small"
                      variant="text"
                      endIcon={<ArrowForwardIcon fontSize="small" />}
                      className="mt-3 pl-0 normal-case text-nexus-purple-light"
                    >
                      {p.linkLabel}
                    </Button>
                  )}
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={360}>
          <div className="mt-8 flex flex-col items-center gap-3 rounded-2xl border border-nexus-purple/25 bg-nexus-purple/[0.06] px-6 py-5 text-center sm:flex-row sm:justify-center sm:text-left">
            <Chip
              label="Dogfooded daily"
              size="small"
              className="flex-shrink-0 border border-nexus-purple/35 bg-transparent font-semibold text-nexus-purple-light"
            />
            <p className="text-sm text-white/75">
              We run our own MSP practice on Nexus — every ticket, every device, every alert,
              for real client organizations — before we ever ask you to.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
